// frontend/src/components/canvasser/calls/CallScriptSheet.tsx
import { useEffect, useRef } from 'react'
import { CloseButton, Drawer, Flex, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import { ConversationEngine } from '../door/conversation/ConversationEngine'
import type { ScriptResult } from '../../../lib/conversation'
import type { ResolvedScriptDto } from '../../../types/canvasser'
import type { ScriptLanguage } from '../../../lib/walkPrefs'

export type CallScriptResult = ScriptResult & { scriptId: string }

/** The conversation key a call's path is saved under (sessionStorage; cleared on confirm and sign-out). */
export const callConversationKey = (batchId: string, itemId: string) => 'call:' + batchId + ':' + itemId

/**
 * [CF-1 P4 §7.6] A scripted call: the door's ConversationEngine over the item's (cached) script. Its finish
 * (or End conversation) hands the ScriptResult on to the outcome sheet. A script that can't be used (not
 * cached offline, none resolved, empty) never dead-ends: onUnavailable sends the caller to the quick outcome.
 */
export function CallScriptSheet({ open, batchId, itemId, name, script, isLoading, isError, language, onFinish, onUnavailable, onClose }: {
  open: boolean; batchId: string; itemId: string; name: string
  script: ResolvedScriptDto | null | undefined; isLoading: boolean; isError: boolean; language: ScriptLanguage
  onFinish: (r: CallScriptResult) => void; onUnavailable: () => void; onClose: () => void
}) {
  const unusable = open && !isLoading && (isError || !script || script.elements.length === 0)
  const onUnavailableRef = useRef(onUnavailable)
  onUnavailableRef.current = onUnavailable
  useEffect(() => { if (unusable) onUnavailableRef.current() }, [unusable])
  const stateKey = callConversationKey(batchId, itemId)
  return (
    <Drawer.Root open={open && !unusable} placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          {/* The engine's own footer pads env(safe-area-inset-bottom, 0px). */}
          <Drawer.Content borderTopRadius="xl" shadow="popover" h="calc(100dvh - 24px)" display="flex" flexDirection="column">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header pb={2}>
              <VStack align="start" gap={0} minW={0}>
                <Drawer.Title lineClamp={1}>{name}</Drawer.Title>
                {script && <Text fontSize="sm" color="fg.muted" lineClamp={1}>{script.scriptLabel}</Text>}
              </VStack>
            </Drawer.Header>
            <Flex direction="column" flex={1} minH={0}>
              {isLoading || !script ? (
                <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
              ) : (
                <ConversationEngine key={stateKey} elements={script.elements} stateKey={stateKey} language={language}
                  onFinish={r => onFinish({ scriptId: script.scriptId, ...r })}
                  onEndEarly={r => onFinish({ scriptId: script.scriptId, ...r })} />
              )}
            </Flex>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
